import { brandPalette } from "../../../constants/brandPalette";
import {
  Avatar,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { MdChevronLeft, MdChevronRight, MdLightbulbOutline } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ExamEllipses from "../../../containers/student/exams/exam-ellipses";
import ExamHint from "../../../containers/student/exams/exam-hint";
import ExamSolvedQuestions from "../../../containers/student/exams/exam-solved-questions";
import { openExamHintkModel } from "../../../modules/admin-modules/models/Actions";
import {
  getExamCorrectionRequest,
  getExamsByIdRequest,
} from "../../../modules/students-modules/exams/Actions";
import { studentInk, studentPalette } from "../../../constants/studentPalette";
import { studentSurfaces } from "../../../constants/studentThemeSurfaces";
import { extractYouTubeVideoId } from "../../../utils/youtubeVideoId";

const FONT_STACK = `'Tajawal','Amiri','Scheherazade New','Noto Sans Arabic','Cairo',sans-serif`;
const SERIF_Q = `'Amiri','Scheherazade New','Noto Sans Arabic',serif`;

const C = {
  primary: studentPalette.primary,
  accent: studentPalette.secondary,
  pageBg: studentSurfaces.canvas,
  border: studentSurfaces.border,
  cream: studentPalette.cream,
  ink: studentInk,
  muted: studentSurfaces.textMuted,
};

const ExamResult = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: examIdParam } = useParams();
  const queryString = require("query-string");
  const parsed = queryString.parse(window.location.search);
  const examId = examIdParam || window.location.pathname.split("/")[2];

  const examCorrection = useSelector((state) => state.chapterExams.examCorrection);
  const isLoading = useSelector((state) => state.chapterExams.isLoading);
  const exam = useSelector((state) => state.chapterExams.exam);
  const userData = useSelector((state) => state.user.userData);

  const [selectedQuestionIdx, setSelectedQuestionIdx] = useState(0);

  const gridPattern = useMemo(
    () => ({
      bg: C.pageBg,
      backgroundImage: `radial-gradient(${brandPalette.stone} 1px, transparent 1px)`,
      backgroundSize: "32px 32px",
    }),
    []
  );

  const qCount = examCorrection?.questions?.length ?? 0;
  const currentQ = examCorrection?.questions?.[selectedQuestionIdx];
  const hintVideoId = useMemo(() => extractYouTubeVideoId(currentQ?.url), [currentQ?.url]);
  const sectionTitle =
    currentQ?.section_name ||
    currentQ?.section?.name ||
    currentQ?.part_name ||
    (parsed?.name ? String(parsed.name) : exam?.name) ||
    "Review";

  const hasHint =
    !!examCorrection?.questions &&
    !!(currentQ?.model_answer_image || currentQ?.url);

  useEffect(() => {
    if (examId) {
      dispatch(getExamsByIdRequest(examId));
      dispatch(getExamCorrectionRequest(examId));
    }
  }, [examId, dispatch]);

  const handelNextQuestion = () => {
    if (selectedQuestionIdx < 0) setSelectedQuestionIdx(qCount - 1);
    else if (selectedQuestionIdx >= qCount - 1) setSelectedQuestionIdx(0);
    else setSelectedQuestionIdx((s) => s + 1);
  };

  const handelPrevQuestion = () => {
    if (selectedQuestionIdx > qCount - 1) setSelectedQuestionIdx(0);
    if (selectedQuestionIdx <= 0) setSelectedQuestionIdx(qCount - 1);
    else setSelectedQuestionIdx((s) => s - 1);
  };

  const handleBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/students/exams");
  };

  if (isLoading) {
    return (
      <Flex minH="100vh" align="center" justify="center" {...gridPattern} fontFamily={FONT_STACK}>
        <Spinner size="xl" thickness="4px" color={C.primary} />
      </Flex>
    );
  }

  return (
    <>
      <Box
        minH="100vh"
        fontFamily={FONT_STACK}
        color="var(--text-primary)"
        {...gridPattern}
        pt={{ base: "88px", md: "96px" }}
        pb="120px"
      >
        <Flex
          as="header"
          position="fixed"
          top={0}
          left={0}
          right={0}
          zIndex={50}
          px={{ base: 4, md: 8 }}
          py={4}
          bg={C.pageBg}
          borderBottomWidth="1px"
          borderColor={C.border}
          align="center"
          justify="space-between"
          gap={4}
          flexWrap="wrap"
        >
          <HStack spacing={3} flex={1} minW={0}>
            <Button
              variant="ghost"
              size="sm"
              fontWeight="800"
              color={C.accent}
              textTransform="uppercase"
              letterSpacing="0.1em"
              fontSize="10px"
              onClick={handleBack}
              _hover={{ bg: "rgba(91, 13, 22, 0.08)" }}
            >
              Back
            </Button>
            <Heading
              as="h2"
              fontSize={{ base: "sm", md: "lg" }}
              fontWeight="800"
              color={C.primary}
              textTransform="uppercase"
              letterSpacing="0.06em"
              noOfLines={2}
            >
              {parsed?.name || exam?.name || "Exam results"}
            </Heading>
          </HStack>
          <HStack spacing={3} flexShrink={0}>
            {exam?.right_answers != null ? (
              <Box
                px={3}
                py={1.5}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={C.border}
                bg={C.cream}
              >
                <Text fontSize="10px" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.12em">
                  Score
                </Text>
                <Text fontWeight="800" color={C.primary} fontSize="md" lineHeight="short">
                  {exam.right_answers}
                  <Text as="span" fontSize="sm" color={C.muted} fontWeight="700">
                    {" "}
                    / {exam?.score ?? exam?.total_count ?? "—"}
                  </Text>
                </Text>
              </Box>
            ) : null}
            <Avatar
              size="sm"
              name={userData?.name || userData?.username || "Student"}
              src={userData?.image || userData?.avatar}
              borderRadius="md"
              borderWidth="1px"
              borderColor={C.border}
            />
          </HStack>
        </Flex>

        <Box maxW="5xl" mx="auto" px={{ base: 4, md: 8 }}>
          <Flex
            align={{ base: "flex-start", md: "flex-end" }}
            justify="space-between"
            gap={4}
            mb={8}
            direction={{ base: "column", md: "row" }}
          >
            <Box>
              <Text
                fontSize="xs"
                fontWeight="800"
                color={`${C.primary}B3`}
                textTransform="uppercase"
                letterSpacing="0.2em"
              >
                Question {qCount ? selectedQuestionIdx + 1 : 0} of {qCount}
              </Text>
              <Heading
                as="h1"
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="800"
                color={C.primary}
                mt={2}
                fontFamily={FONT_STACK}
                lineHeight="1.15"
              >
                {sectionTitle}
              </Heading>
            </Box>
            <HStack spacing={1}>
              <IconButton
                aria-label="Previous question"
                icon={<MdChevronLeft size={28} />}
                variant="outline"
                borderColor={C.border}
                bg={studentSurfaces.raised}
                color={C.primary}
                borderRadius="lg"
                onClick={handelPrevQuestion}
                sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
              />
              <IconButton
                aria-label="Next question"
                icon={<MdChevronRight size={28} />}
                variant="outline"
                borderColor={C.border}
                bg={studentSurfaces.raised}
                color={C.primary}
                borderRadius="lg"
                onClick={handelNextQuestion}
                sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
              />
            </HStack>
          </Flex>

          {hasHint ? (
            <Flex justify="flex-end" mb={4}>
              <Button
                leftIcon={<MdLightbulbOutline size={18} />}
                bg={C.primary}
                color="white"
                px={{ base: 8, md: 14 }}
                py={6}
                borderRadius="xl"
                fontWeight="800"
                textTransform="uppercase"
                letterSpacing="0.12em"
                fontSize="sm"
                _hover={{ bg: C.accent }}
                boxShadow="md"
                onClick={() => dispatch(openExamHintkModel())}
              >
                Hint
              </Button>
            </Flex>
          ) : null}

          <VStack spacing={6} align="stretch" w="100%">
            {examCorrection?.questions?.length ? (
              currentQ ? (
                <ExamSolvedQuestions
                  data={currentQ}
                  correctionArr={examCorrection?.answers}
                  selectedQuestionIdx={selectedQuestionIdx}
                  design="academic"
                  questionSerifFont={SERIF_Q}
                />
              ) : null
            ) : (
              <Text color={C.muted} fontWeight="600">
                No correction data available.
              </Text>
            )}

            <Flex justify="flex-end" align="center" flexWrap="wrap" gap={4}>
              <Button
                bg={C.primary}
                color="white"
                px={{ base: 8, md: 14 }}
                py={6}
                borderRadius="xl"
                fontWeight="800"
                textTransform="uppercase"
                letterSpacing="0.12em"
                fontSize="sm"
                _hover={{ bg: C.accent }}
                boxShadow="md"
                onClick={handelNextQuestion}
              >
                Next question
              </Button>
            </Flex>
          </VStack>
        </Box>

        <Flex
          as="footer"
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          zIndex={40}
          bg={studentSurfaces.raised}
          borderTopWidth="1px"
          borderColor={C.border}
          px={{ base: 3, md: 8 }}
          py={3}
          align="center"
          justify="space-between"
          flexWrap="wrap"
          gap={3}
          boxShadow="0 -4px 10px rgba(0,0,0,0.05)"
        >
          <HStack
            spacing={1.5}
            overflowX="auto"
            pb={1}
            css={{
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
            flex="1"
            minW={0}
          >
            <IconButton
              aria-label="Previous"
              icon={<MdChevronLeft />}
              variant="ghost"
              color={C.primary}
              flexShrink={0}
              onClick={handelPrevQuestion}
            />
            <ExamEllipses
              examCorrection={examCorrection}
              questionsCount={qCount}
              setSelectedQuestionIdx={setSelectedQuestionIdx}
              selectedQuestionIdx={selectedQuestionIdx}
              accentColor={C.primary}
            />
            <IconButton
              aria-label="Next"
              icon={<MdChevronRight />}
              variant="ghost"
              color={C.primary}
              flexShrink={0}
              onClick={handelNextQuestion}
            />
          </HStack>
          <HStack spacing={2} display={{ base: "none", sm: "flex" }} flexShrink={0}>
            <HStack spacing={2}>
              <Box w="10px" h="10px" borderRadius="full" bg="green.500" />
              <Text fontSize="10px" fontWeight="800" color={C.muted} textTransform="uppercase">
                Correct
              </Text>
            </HStack>
            <HStack spacing={2}>
              <Box w="10px" h="10px" borderRadius="full" bg="red.500" />
              <Text fontSize="10px" fontWeight="800" color={C.muted} textTransform="uppercase">
                Incorrect
              </Text>
            </HStack>
          </HStack>
        </Flex>
      </Box>

      <ExamHint
        videoId={hintVideoId}
        model_answer_image={examCorrection?.questions?.[selectedQuestionIdx]?.model_answer_image}
      />
    </>
  );
};

export default ExamResult;
